import type { LiqCluster, LiqMapSnapshot } from '@/lib/api/speed';

import type { PositionType } from '../trading/types';

import { formatNotional, type TradeCheck } from './clusters';

/**
 * The plain-English layer. A professional reads a liquidation map in two
 * glances; a beginner needs the two sentences the professional is thinking.
 * Every sentence here says three things and nothing else: the price, the
 * money, and what usually happens next.
 */

/** A price level at the coin's own scale: "78,006.1", "12.24", "0.08123". */
export function formatLevelPrice(v: number): string {
    if (v >= 1000) {
        return v.toLocaleString('en-US', { maximumFractionDigits: 1 });
    }
    if (v >= 1) return v.toFixed(2);
    return v.toPrecision(4);
}

const pct = (v: number): string => `${Math.abs(v).toFixed(1)}%`;

/** One cluster, explained. */
export function explainCluster(
    coin: string,
    cluster: LiqCluster,
    side: 'long' | 'short',
): string {
    const money = formatNotional(cluster.notionalUsd);
    const at = `$${formatLevelPrice(cluster.price)}`;
    if (side === 'long') {
        return `If ${coin} falls ${pct(cluster.distancePct)} to ${at}, about ${money} of longs get force-closed. Forced selling usually pushes price down faster from there.`;
    }
    return `If ${coin} rises ${pct(cluster.distancePct)} to ${at}, about ${money} of shorts get force-closed. Forced buying usually pushes price up faster from there.`;
}

/** The one-line verdict on the trader's own exit levels. */
export function explainCheck(
    check: TradeCheck,
    type: PositionType,
): { tone: 'danger' | 'warn' | 'ok'; text: string } {
    if (check.kind === 'clear' || !check.cluster) {
        return {
            tone: 'ok',
            text:
                type === 'LONG'
                    ? 'Your stop and liquidation sit clear of the big long clusters below.'
                    : 'Your stop and liquidation sit clear of the big short clusters above.',
        };
    }
    const money = formatNotional(check.cluster.notionalUsd);
    const cluster = `$${formatLevelPrice(check.cluster.price)}`;
    const suggestion =
        check.suggestedStop !== null
            ? ` Consider $${formatLevelPrice(check.suggestedStop)}, just past it.`
            : '';
    if (check.kind === 'stopInside') {
        return {
            tone: 'danger',
            text: `Your stop-loss sits inside a ${money} liquidation cluster at ${cluster}. Stops placed there tend to get swept by the same move that triggers it.${suggestion}`,
        };
    }
    if (check.kind === 'stopNear') {
        return {
            tone: 'warn',
            text: `Your stop-loss is ${pct(check.gapPct ?? 0)} from a ${money} liquidation cluster at ${cluster}. The wick that clears that cluster often reaches this far.${suggestion}`,
        };
    }
    if (check.kind === 'liqInside') {
        return {
            tone: 'danger',
            text: `Your own liquidation sits inside a ${money} cluster at ${cluster}. If that cluster goes, you go with it. Lower leverage or add margin to move it.`,
        };
    }
    return {
        tone: 'warn',
        text: `Your liquidation is ${pct(check.gapPct ?? 0)} from a ${money} cluster at ${cluster}. A cascade there would likely reach you.`,
    };
}

/** The headline for the legend: how much sits within reach on each side. */
export function explainReach(
    coin: string,
    snapshot: LiqMapSnapshot,
    within = 3,
): string {
    const down = snapshot.cascade.down.find(([p]) => p === within)?.[1] ?? 0;
    const up = snapshot.cascade.up.find(([p]) => p === within)?.[1] ?? 0;
    return `Within ${within}% of the current price: ${formatNotional(down)} of longs below, ${formatNotional(up)} of shorts above.`;
}

/** What the sample is, so the number is trusted for the right reason. */
export function explainSource(snapshot: LiqMapSnapshot): string {
    const wallets = snapshot.sample.addresses ?? 0;
    const positions = snapshot.sample.positions ?? 0;
    const age = Math.round(snapshot.ageMs / 60_000);
    const when = age < 1 ? 'just now' : `${age} min ago`;
    return `Real liquidation prices from ${positions.toLocaleString('en-US')} open positions across ${wallets.toLocaleString('en-US')} of Hyperliquid's largest accounts, ${when}.`;
}
